/**
 * In-app help (client layer `help/`): one Markdown page per screen or task, in `help/*.md` with front matter, read
 * by the sync step into `help.pages`. `help/whats-new.md` also gives `help.whatsNew`, its dated entries. The
 * platform supplies no wording: the pages are the client's own. See docs/HELP.md.
 *
 * Front matter (one `key: value` per line, values optionally quoted):
 *   id        unique, lower case, e.g. "leads"
 *   title     "Leads"
 *   app       staff (the staff portal) or customer (the customer portal); "all" only for whats-new.md
 *   route     the screen it explains, a hash route in that app ("#/leads", "#/project"), or a web address
 *   audience  staff: all, director, admin, office or crew. customer: all, owner, member or accounts.
 *             Staff "office" pages are for every role but controllers (crew logins); "crew" pages are for crew and
 *             shown to everyone else too, like "all".
 *   summary   one line, shown in lists, empty states and at the top of the panel
 *   updated   YYYY-MM-DD
 *   platform  the platform version it was written against, e.g. "v0.15.0" (optional)
 *   guide     "no" (optional): never opened as the screen's guide by How this works; it is still listed under
 *             Related on that screen, in Help and in search. For a second page on a screen, such as an owner-only
 *             page beside the guide everyone uses.
 *
 * What's new entries are "## <date>" headings, newest first. An entry for one app starts with a line
 * "App: staff" or "App: customer"; without it the entry shows in both apps. A release not out yet can have a
 * heading of just its version ("## v0.19.0") until it reaches users; the date goes in then
 * ("## 4 December 2026 (v0.19.0)") and anyone who saw the undated entry has still seen it.
 *
 * Also `support`: where "Something's not working" reports go.
 *
 * Like schema.ts, this file only imports zod, so the sync step can load it with Node's TypeScript support.
 */
import { z } from "zod";

export const HELP_APPS = ["staff", "customer", "all"] as const;
export type HelpApp = (typeof HELP_APPS)[number];
/** The two apps a page or entry is shown in. */
export type HelpAppName = Exclude<HelpApp, "all">;

/**
 * Staff portal screens a page can belong to: one per portal module (schema.ts PORTAL_MODULES, checked by a test),
 * and the Help section.
 */
// vocab-ok: module routes (the field-services modules are named for what they manage)
export const STAFF_HELP_ROUTES = [
  // vocab-ok: module routes (the field-services modules are named for what they manage)
  "#/today", "#/my", "#/leads", "#/clients", "#/quotes", "#/jobs", "#/bookings", "#/invoices", "#/pay", "#/crew", "#/equipment", "#/incidents",
  "#/customers", "#/crm", "#/rates", "#/terms", "#/integrations", "#/help",
] as const;
/** Customer portal screens: Overview is "#/", a milestone ("#/milestones/<id>") is "#/milestones". */
export const CUSTOMER_HELP_ROUTES = ["#/", "#/project", "#/milestones", "#/feedback", "#/requests", "#/services", "#/status", "#/ownership", "#/documents", "#/team", "#/help"] as const;

// vocab-ok: the field-services help audience (site staff)
export const STAFF_AUDIENCES = ["all", "director", "admin", "office", "crew"] as const;
export const CUSTOMER_AUDIENCES = ["all", "owner", "member", "accounts"] as const;
export type HelpAudience = (typeof STAFF_AUDIENCES)[number] | (typeof CUSTOMER_AUDIENCES)[number];

const text = z.string().trim().min(1);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");
const noDashes = (s: string) => !/[\u2013\u2014]/.test(s);
const webAddress = (r: string) => /^https:\/\/[^\s]+$/.test(r);
const routesOf = (app: HelpApp): readonly string[] =>
  app === "staff" ? STAFF_HELP_ROUTES : app === "customer" ? CUSTOMER_HELP_ROUTES : [...new Set([...STAFF_HELP_ROUTES, ...CUSTOMER_HELP_ROUTES])];
const audiencesOf = (app: HelpApp): readonly string[] => (app === "staff" ? STAFF_AUDIENCES : app === "customer" ? CUSTOMER_AUDIENCES : ["all"]);

export const helpPageSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,59}$/, 'Expected a lower case id such as "leads"'),
    title: text.max(120),
    app: z.enum(HELP_APPS, { errorMap: () => ({ message: 'Expected app "staff" or "customer" ("all" only for whats-new.md)' }) }),
    route: z.string(),
    audience: z.string(),
    summary: text.max(300),
    updated: isoDate,
    platform: z.string().regex(/^v\d+\.\d+\.\d+$/, "Expected a version such as v0.15.0").optional(),
    guide: z.enum(["no"], { errorMap: () => ({ message: 'Expected guide "no" (or leave it out)' }) }).optional(),
    /** The page's Markdown, after the front matter. */
    body: z.string(),
  })
  .superRefine((p, ctx) => {
    const routes = routesOf(p.app);
    if (!routes.includes(p.route) && !webAddress(p.route)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["route"], message: `Unknown ${p.app === "all" ? "" : `${p.app} `}route "${p.route}". Use one of ${routes.join(", ")} or a web address.` });
    }
    const audiences = audiencesOf(p.app);
    if (!audiences.includes(p.audience)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["audience"], message: `Audience "${p.audience}" is not one of ${audiences.join(", ")} for app "${p.app}"` });
    }
    for (const k of ["title", "summary", "body"] as const) {
      if (!noDashes(p[k])) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [k], message: `Help page "${p.id}": use a comma, colon or full stop instead of an em or en dash` });
    }
    if (p.app === "all" && p.id !== "whats-new") ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["app"], message: 'Only whats-new.md can be in "all" apps; give this page app "staff" or "customer"' });
  });
export type HelpPage = z.output<typeof helpPageSchema>;

export const whatsNewEntrySchema = z.object({
  /** YYYY-MM-DD, from the heading's date, or "" for a release not out yet (a heading of just the version). Newest first. */
  date: z.string().regex(/^(\d{4}-\d{2}-\d{2})?$/, "Expected YYYY-MM-DD"),
  /** The heading as written, e.g. "4 November 2026 (v0.15.0)". */
  title: text,
  /** Which app shows it ("App: staff" as the entry's first line). Default both. */
  app: z.enum(HELP_APPS).default("all"),
  body: z.string(),
});
export type WhatsNewEntry = z.output<typeof whatsNewEntrySchema>;

export const helpSchema = z
  .object({
    pages: z.array(helpPageSchema).default([]),
    whatsNew: z.array(whatsNewEntrySchema).default([]),
  })
  .superRefine((h, ctx) => {
    const seen = new Set<string>();
    h.pages.forEach((p, i) => {
      if (seen.has(p.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["pages", i, "id"], message: `Help page id "${p.id}" is used twice` });
      seen.add(p.id);
    });
    for (let i = 1; i < h.whatsNew.length; i++) {
      // Undated entries (releases not out yet) come first, then dated ones newest first.
      const cur = h.whatsNew[i]!.date, prev = h.whatsNew[i - 1]!.date;
      if (prev && (!cur || cur > prev)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["whatsNew", i], message: "What's new entries go newest first, with any undated ones (not released yet) at the top" });
    }
  });
export type HelpConfig = z.output<typeof helpSchema>;

export const supportSchema = z.object({
  /** Where "Something's not working" reports are emailed. */
  email: z.string().email(),
  /** Who looks after the app, as in "Thanks, {team} has your report." */
  team: text.max(60).refine(noDashes, "No em or en dashes in text users see"),
  confirmation: text.refine(noDashes, "No em or en dashes in text users see").default("Thanks, {team} has your report."),
});
export type SupportConfig = z.output<typeof supportSchema>;

/* ------------------------------------------------------------------ parsing (used by the sync step) */

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

/** Split a help file into its front matter and body. Throws with the file name on a malformed file. */
export function parseHelpFile(name: string, src: string): Record<string, string> & { body: string } {
  const s = src.replace(/\r\n?/g, "\n").replace(/^\uFEFF/, "");
  const m = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/.exec(s);
  if (!m) throw new Error(`help/${name}: expected front matter between --- lines at the top`);
  const meta: Record<string, string> = {};
  for (const line of m[1]!.split("\n")) {
    if (!line.trim() || line.trim().startsWith("#")) continue;
    const kv = /^([a-zA-Z]+):\s*(.*)$/.exec(line);
    if (!kv) throw new Error(`help/${name}: cannot read front matter line "${line}"`);
    let v = kv[2]!.trim();
    if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) v = v.slice(1, -1);
    meta[kv[1]!] = v;
  }
  return { ...meta, body: m[2]!.trim() };
}

/** "4 November 2026" or "31 October 2026 (v0.8.5)" to "2026-11-04". */
export function headingDate(heading: string): string | null {
  const m = /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})\b/.exec(heading.trim());
  if (!m) return null;
  const month = MONTHS.indexOf(m[2]!.toLowerCase());
  if (month < 0) return null;
  return `${m[3]}-${String(month + 1).padStart(2, "0")}-${m[1]!.padStart(2, "0")}`;
}

/** What's new entries: each "## <date>" heading, an optional "App: staff|customer|all" line, and the text under it. */
export function parseWhatsNew(body: string): { date: string; title: string; app: HelpApp; body: string }[] {
  const out: { date: string; title: string; app: HelpApp; body: string }[] = [];
  let cur: { date: string; title: string; lines: string[] } | null = null;
  const finish = () => {
    if (!cur) return;
    const lines = [...cur.lines];
    while (lines.length && !lines[0]!.trim()) lines.shift();
    let app: HelpApp = "all";
    const marker = /^app:\s*(\S+)\s*$/i.exec(lines[0] ?? "");
    if (marker) {
      const v = marker[1]!.toLowerCase();
      if (!(HELP_APPS as readonly string[]).includes(v)) throw new Error(`help/whats-new.md: "${lines[0]!.trim()}" under "${cur.title}" should be App: staff, App: customer or App: all`);
      app = v as HelpApp;
      lines.shift();
    }
    out.push({ date: cur.date, title: cur.title, app, body: lines.join("\n").trim() });
  };
  for (const line of body.split("\n")) {
    const h = /^##\s+(.+)$/.exec(line.trim());
    if (h) {
      finish();
      // A release not out yet has no date: its heading is just the version.
      const date = /^v\d+\.\d+\.\d+\b/.test(h[1]!.trim()) ? "" : headingDate(h[1]!);
      if (date === null) throw new Error(`help/whats-new.md: heading "${h[1]}" should start with a date such as "4 November 2026", or be just the version ("v0.19.0") until it is released`);
      cur = { date, title: h[1]!.trim(), lines: [] };
    } else if (cur) cur.lines.push(line);
  }
  finish();
  return out;
}

/** Getting started first (id "getting-started" or ending in "-getting-started"), then by title. */
export const isGettingStarted = (p: { id: string }) => p.id === "getting-started" || p.id.endsWith("-getting-started");
export function sortHelpPages<T extends { id: string; title: string }>(pages: readonly T[]): T[] {
  return [...pages].sort((a, b) => Number(isGettingStarted(b)) - Number(isGettingStarted(a)) || a.title.localeCompare(b.title));
}

/* ------------------------------------------------------------------ who sees what */

/**
 * The pages one user may see in one app. Directors see every staff page, admins see "all", "admin", "office" and
 * "crew" pages, controllers (crew logins) "all" and "crew" pages, other staff roles every page but "director" and
 * "admin" ones. Customer owners see every customer page; members and accounts logins see "all" pages
 * and their own role's pages.
 */
export function pagesFor<T extends { app: HelpApp; audience: string }>(pages: readonly T[], app: HelpAppName, role: string): T[] {
  return pages.filter((p) => (p.app === app || p.app === "all") && canSee(p.audience, app, role));
}
function canSee(audience: string, app: HelpAppName, role: string): boolean {
  if (audience === "all") return true;
  if (app === "staff") {
    // vocab-ok: the field-services help audience
    if (audience === "crew") return true;
    if (audience === "office") return role !== "controller";
    return role === "director" || audience === role;
  }
  return role === "owner" || audience === role;
}

/** What's new entries for one app, newest first. */
export const entriesFor = <T extends { app: HelpApp }>(entries: readonly T[], app: HelpAppName): T[] => entries.filter((e) => e.app === app || e.app === "all");

/** The What's new page body for one app: its entries as written, without the App lines. */
export const whatsNewBody = (entries: readonly { title: string; body: string }[]) => entries.map((e) => `## ${e.title}\n${e.body}`).join("\n\n");

/** How a What's new entry is remembered as seen: its date and heading (two entries can share a date). */
export const entryKey = (e: { date: string; title: string }) => `${e.date} ${e.title}`;

/** Entries newer than the one last seen: by key, or by date if that entry has since been renamed or removed. */
export function unseenSince<T extends { date: string; title: string }>(entries: readonly T[], seen: string | null): T[] {
  if (!seen) return [...entries];
  let i = entries.findIndex((e) => entryKey(e) === seen);
  // The same release, since dated ("v0.19.0" became "4 December 2026 (v0.19.0)"): still seen.
  const version = /v\d+\.\d+\.\d+/.exec(seen)?.[0];
  if (i < 0 && version) i = entries.findIndex((e) => e.title.includes(version));
  return i >= 0 ? entries.slice(0, i) : entries.filter((e) => !e.date || e.date > seen.slice(0, 10));
}

/** The screen a hash belongs to in an app: "#/leads/123" is "#/leads"; customer "#/milestones/m1" is "#/milestones". */
export function helpRouteOf(hash: string, app: HelpAppName): string {
  const base = `#/${hash.replace(/^#\/?/, "").split(/[/?]/)[0] ?? ""}`;
  if (app === "customer") return (CUSTOMER_HELP_ROUTES as readonly string[]).includes(base) ? base : "#/";
  return base === "#/" ? "#/today" : base;
}
